import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram } from "lucide-react";

const DISCLAIMER = "Veterans Disability Education provides education, coaching, and strategic guidance only. Pamela J. Booker is not a VA-accredited claims agent, attorney, or Veterans Service Organization representative, and does not prepare, present, or prosecute VA claims on behalf of veterans. Nothing on this site is legal or medical advice. Veterans Disability Education is not affiliated with or endorsed by the U.S. Department of Veterans Affairs. Free help filing claims is available from accredited VSOs. Results vary; no outcome is guaranteed.";

const FACEBOOK_URL = "https://www.facebook.com/veteransdisabilityeducation";
const INSTAGRAM_URL = "https://www.instagram.com/veteransdisabilityeducation";

export default function Footer() {
  return (
    <footer className="mt-20 bg-primary text-primary-foreground" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <img src="/vde-logo-white.png" alt="Veterans Disability Education" className="h-11 w-auto" />
            <span className="font-display text-lg leading-tight border-l-2 border-accent pl-3">
              Veterans Disability<br />Education
            </span>
          </div>
          <p className="mt-5 max-w-md text-primary-foreground/85 text-[15px] leading-relaxed">
            Real strategy, not guesswork. Founded by Pamela J. Booker, a 100% rated U.S. Army veteran teaching fellow veterans how to build strong, evidence-based VA disability claims.
          </p>
          <p className="mt-4 font-display text-base text-accent tracking-wide" data-testid="footer-signature">
            Educated. Advocated. Compensated.
          </p>
          <div className="mt-5 flex items-center gap-3" data-testid="footer-socials">
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Veterans Disability Education on Facebook"
              className="w-10 h-10 grid place-items-center border border-primary-foreground/30 hover:bg-accent hover:text-accent-foreground hover:border-accent transition-colors"
              data-testid="footer-facebook"
            >
              <Facebook className="w-4 h-4" strokeWidth={1.8} />
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Veterans Disability Education on Instagram"
              className="w-10 h-10 grid place-items-center border border-primary-foreground/30 hover:bg-accent hover:text-accent-foreground hover:border-accent transition-colors"
              data-testid="footer-instagram"
            >
              <Instagram className="w-4 h-4" strokeWidth={1.8} />
            </a>
          </div>
        </div>
        <div className="md:col-span-3">
          <div className="label-caps text-accent mb-4">Site</div>
          <ul className="space-y-2 text-[15px]">
            <li><Link to="/calculator" className="hover:text-accent">VA Pay Calculator</Link></li>
            <li><Link to="/learn" className="hover:text-accent">Learn</Link></li>
            <li><Link to="/work-with-me" className="hover:text-accent">Work With Me</Link></li>
            <li><Link to="/about" className="hover:text-accent">About</Link></li>
            <li><Link to="/contact" className="hover:text-accent">Contact</Link></li>
          </ul>
        </div>
        <div className="md:col-span-4">
          <div className="label-caps text-accent mb-4">Ready when you are</div>
          <p className="text-[15px] text-primary-foreground/85 mb-4">Start with a free 20-minute consult. Bring your questions.</p>
          <Link to="/intake" className="inline-block bg-accent text-accent-foreground px-6 py-3 font-semibold hover:bg-accent/90" data-testid="footer-cta-intake">
            Start Your Free Consult →
          </Link>
        </div>
      </div>

      <div className="border-t border-primary-foreground/20">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-6">
          <p className="text-xs text-primary-foreground/70 leading-relaxed max-w-5xl" data-testid="site-disclaimer">
            {DISCLAIMER}
          </p>
          <div className="mt-4 flex flex-col sm:flex-row justify-between gap-2 text-xs text-primary-foreground/60">
            <span>© {new Date().getFullYear()} Veterans Disability Education. All rights reserved.</span>
            <span className="flex gap-4">
              <Link to="/privacy" className="hover:text-accent">Privacy</Link>
              <Link to="/terms" className="hover:text-accent">Terms</Link>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
